// Cena na detailu produktu — jediné místo, kde se čte, přepočítává a formátuje.
// Používá ji CTA v buy-boxu (`productDetail.js`) i sticky ATC lišta (`stickyAtc.js`),
// ať nikde nevzniká druhá varianta výpočtu „qty × cena".
//
// Zásada: cena se NEBERE z microdata (`meta[itemprop=price]`), ale z živého
// `.price-final` — u variantních produktů ho default JS Shoptetu přepisuje za běhu
// a my chceme jet s ním (MutationObserver). Množství čteme z nativního
// `input.amount`; nativní ± tlačítka hodnotu mění programově, proto kromě
// `input`/`change` posloucháme i klik na `.increase`/`.decrease`.

import { NBSP, currency as shopCurrency, number, withCurrency } from './money.js';

// „1 899,50 Kč / ks" → 1899.5 (mezery i nbsp jako oddělovač tisíců, desetinná čárka)
export function parseNumber(value) {
  const m = String(value || '')
    .replace(/\u00A0/g, ' ')
    .match(/\d[\d ]*(?:[.,]\d+)?/);
  if (!m) return NaN;
  return parseFloat(m[0].replace(/ /g, '').replace(',', '.'));
}

// Měnu bereme z textu, ne z konstanty — shop může mít jazykové mutace.
export function currencyOf(value) {
  const m = String(value || '')
    .replace(/\u00A0/g, ' ')
    // symbol vpravo nesmí začínat interpunkcí: u „€20,45" by backtrack vzal
    // jako měnu čárku (ostrá SK 25. 9.–14. 10.: „Do košíka · 20,45 ,")
    .match(/\d[\d .,]*\s*([^\d\s/.,][^\d\s/]*)/);
  if (m) return m[1];
  // symbol vlevo od čísla („€6,11" na SK)
  const left = String(value || '').match(/([^\d\s/.,]+)\s*\d/);
  return left ? left[1] : shopCurrency().symbol;
}

// Haléře vždy na DVĚ místa jako Shoptet („319,20 Kč", ne „319,2 Kč") — ceny
// s haléři vidí hlavně velkoobchodní zákazník (VO ceník = MOC × koeficient).
export function formatPrice(value, currency) {
  if (!isFinite(value)) return '';
  // měna shopu jde na svou stranu („1 899 Kč" / „€6,11"), cizí symbol z textu za číslo
  if (!currency || currency === shopCurrency().symbol) return withCurrency(number(value));
  return number(value) + NBSP + currency;
}

/**
 * Procenta slevy jako Shoptet: zaokrouhluje DOLŮ (ověřeno 2026-08-26 na 13
 * rozhodujících dvojicích z výpisu dev — 1 999 → 1 749 Kč = 12,5 % píše „–12 %",
 * `Math.round` dával 13 a badge detailu se rozcházel s nativní vlaječkou).
 * Počítá se v celých procentech z rozdílu, ne z podílu — `0.29 * 100` je
 * 28.999…, floor by dal 28. Vrací `null`, když sleva není.
 */
export function discountPercent(standard, current) {
  const std = Number(standard);
  const now = Number(current);
  if (!isFinite(std) || !isFinite(now) || std <= 0 || now < 0 || std <= now) return null;
  const pct = Math.floor(((std - now) * 100) / std);
  return pct > 0 ? pct : null;
}

// Sleduje jednotkovou cenu i množství a hlásí přepočtený součet.
// Vrací { subscribe(fn), refresh(), state }.
/**
 * Text ceny, kterou zákazník právě vidí. Variantní produkt nese v `.price-final`
 * (i `.price-additional`) jeden `span.choose-variant.<index>` na variantu plus
 * `.default-variant` („od 259 Kč") a default JS jim při volbě jen přepíná
 * `no-display` — `textContent` celého prvku je tedy „259 Kč / ks 269 Kč / ks …"
 * a `parseNumber` z něj vzal vždy cenu PRVNÍ varianty (do 2026-09-24: CTA holin
 * „Do košíku · 259 Kč" u velikosti za 269 Kč).
 */
export function visiblePriceText(el) {
  if (!el) return '';
  const parts = el.querySelectorAll('.choose-variant');
  if (!parts.length) return el.textContent;
  for (let i = 0; i < parts.length; i += 1) {
    if (!parts[i].classList.contains('no-display')) return parts[i].textContent;
  }
  return '';
}

export function createPriceWatch(form) {
  const finalEl = form.querySelector('.price-final');
  const standardEl = form.querySelector('.price-standard');
  const amount = form.querySelector('input.amount');
  const listeners = [];
  const state = { unit: NaN, qty: 1, total: NaN, currency: shopCurrency().symbol, text: '', standard: NaN, standardText: '' };

  function readUnit() {
    const raw = visiblePriceText(finalEl);
    const value = parseNumber(raw);
    if (isFinite(value)) {
      state.unit = value;
      state.currency = currencyOf(raw) || state.currency;
    }
    state.standard = standardEl ? parseNumber(visiblePriceText(standardEl)) : NaN;
  }

  function readQty() {
    const value = parseInt(amount ? amount.value : '1', 10);
    state.qty = isFinite(value) && value > 0 ? value : 1;
  }

  function emit() {
    readUnit();
    readQty();
    state.total = isFinite(state.unit) ? state.unit * state.qty : NaN;
    state.text = formatPrice(state.total, state.currency);
    state.standardText = isFinite(state.standard) ? formatPrice(state.standard * state.qty, state.currency) : '';
    listeners.forEach(function (fn) {
      fn(state);
    });
  }

  if (amount) {
    ['input', 'change'].forEach(function (evt) {
      amount.addEventListener(evt, emit);
    });
  }
  form.querySelectorAll('.quantity .increase, .quantity .decrease').forEach(function (btn) {
    // hodnotu nastavuje default JS až po svém handleru → čti v dalším rámci
    btn.addEventListener('click', function () {
      requestAnimationFrame(emit);
    });
  });
  if (finalEl) {
    // `class` kvůli variantám: volba jen přepne `no-display` mezi `.choose-variant`
    new MutationObserver(emit).observe(finalEl, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
  }

  emit();

  return {
    state: state,
    refresh: emit,
    subscribe: function (fn) {
      listeners.push(fn);
      fn(state);
    },
  };
}
